import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { messageService } from '../services/api';

interface Message {
  id: number;
  senderId: number;
  messageText: string;
  createdAt: string;
  isEdited: boolean;
  sender: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
  readReceipts: Array<{
    userId: number;
    readAt: string;
  }>;
}

interface TransactionChatProps {
  transactionId: string;
  currentUserId: number;
  otherUserName: string;
}

const TransactionChat = ({ transactionId, currentUserId, otherUserName }: TransactionChatProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef<number>(0);

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const response = await messageService.getTransactionMessages(transactionId);
      const newMessages = response.data.data;
      setMessages(newMessages);
      setError(null);

      // Mark unread messages as read
      const unreadMessages = newMessages.filter(
        (msg: Message) =>
          msg.senderId !== currentUserId &&
          !msg.readReceipts.some(receipt => receipt.userId === currentUserId)
      );

      for (const msg of unreadMessages) {
        try {
          await messageService.markAsRead(msg.id);
        } catch (err) {
          console.error('Failed to mark message as read:', err);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch messages:', err);
      console.error('Failed to fetch messages:', err);
      setError(t('chat.error.load'));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchMessages();
  }, [transactionId]);

  // Polling for new messages every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMessages();
    }, 10000);

    return () => clearInterval(interval);
  }, [transactionId]);

  // Auto-scroll to bottom only when new messages arrive (not on every poll)
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await messageService.sendMessage({
        transactionId: Number(transactionId),
        messageText: newMessage.trim(),
      });

      setNewMessage('');
      await fetchMessages(); // Refresh messages
    } catch (err: any) {
      console.error('Failed to send message:', err);
      console.error('Failed to send message:', err);
      setError(err.response?.data?.message || t('chat.error.send'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('chat.time.justNow');
    if (diffMins < 60) return t('chat.time.ago', { time: `${diffMins}${t('chat.time.m')}` });
    if (diffHours < 24) return t('chat.time.ago', { time: `${diffHours}${t('chat.time.h')}` });
    if (diffDays < 7) return t('chat.time.ago', { time: `${diffDays}${t('chat.time.d')}` });

    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4">{t('chat.title')}</h2>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-4">{t('chat.title')}</h2>
      <p className="text-sm text-gray-600 mb-4">
        {t('chat.subtitle', { name: otherUserName })}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        className="border border-gray-200 rounded-lg p-4 h-96 overflow-y-auto mb-4 bg-gray-50"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>{t('chat.noMessages')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const isOwn = message.senderId === currentUserId;
              const isRead = message.readReceipts.some(receipt => receipt.userId !== currentUserId);

              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                    {!isOwn && (
                      <div className="flex items-center gap-2 mb-1">
                        {message.sender.profilePicture && (
                          <img
                            src={message.sender.profilePicture}
                            alt={message.sender.displayName}
                            className="w-6 h-6 rounded-full"
                          />
                        )}
                        <span className="text-xs font-medium text-gray-700">
                          {message.sender.displayName}
                        </span>
                      </div>
                    )}
                    <div
                      className={`rounded-lg px-4 py-2 ${isOwn
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                        }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.messageText}
                      </p>
                      {message.isEdited && (
                        <span className="text-xs opacity-70 italic">{t('chat.edited')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs ${isOwn ? 'text-gray-600' : 'text-gray-500'}`}>
                        {formatTime(message.createdAt)}
                      </span>
                      {isOwn && isRead && (
                        <span className="text-xs text-gray-500">✓ {t('chat.read')}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t('chat.placeholder')}
          disabled={sending}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={sending || !newMessage.trim()}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
        >
          {sending ? t('chat.sending') : t('chat.send')}
        </button>
      </form>
    </div>
  );
};

export default TransactionChat;
